import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Star } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";
const publicApi = axios.create({ baseURL: API_BASE, withCredentials: false });

// Public post-installation review — no auth. Token in URL is the anti-forgery.
export default function ReviewPublic() {
  const token = window.location.pathname.split("/").pop();
  const [state, setState] = useState({
    product_quality: 0, delivery_time: 0, coordination: 0, installation: 0, comments: "",
  });
  const [meta, setMeta] = React.useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    publicApi.get(`/project/review/${token}`)
      .then((r) => setMeta(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Review link is invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if ([state.product_quality, state.delivery_time, state.coordination, state.installation].some((r) => r < 1)) {
      setError("Please rate all four categories."); return;
    }
    setError(""); setSubmitting(true);
    try {
      await publicApi.post(`/project/review/${token}`, state);
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong");
    } finally { setSubmitting(false); }
  };

  if (error && !meta) return <Frame><div className="text-red-600 text-center">{error}</div></Frame>;
  if (!meta) return <Frame><div className="text-slate-500 text-center">Loading…</div></Frame>;
  if (meta.already_submitted || success) {
    return (
      <Frame>
        <div className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>Thanks for your feedback.</h1>
          <p className="text-slate-600 mt-3">We&apos;ll use this to keep improving.</p>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <div className="hk-tiny-label" style={{ color: "#E52B50" }}>Post-installation review</div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>
        How did we do, {meta.customer_name}?
      </h1>
      <p className="text-slate-600 mt-2">Project <span className="font-mono font-bold">{meta.project_id}</span></p>

      <form onSubmit={submit} className="mt-8 space-y-6">
        <StarRow label="Product quality" value={state.product_quality} onChange={(v) => setState({ ...state, product_quality: v })} testId="rate-quality" />
        <StarRow label="Delivery time" value={state.delivery_time} onChange={(v) => setState({ ...state, delivery_time: v })} testId="rate-delivery" />
        <StarRow label="Coordination" value={state.coordination} onChange={(v) => setState({ ...state, coordination: v })} testId="rate-coord" />
        <StarRow label="Installation" value={state.installation} onChange={(v) => setState({ ...state, installation: v })} testId="rate-install" />
        <div>
          <label className="block text-sm font-semibold text-slate-800 mb-1.5">Comments</label>
          <textarea rows={4} className="w-full border border-slate-300 rounded-sm p-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            placeholder="Anything else you'd like to share?"
            value={state.comments} onChange={(e) => setState({ ...state, comments: e.target.value })} data-testid="rate-comments" />
        </div>
        {error && <div className="text-red-700 bg-red-50 border border-red-200 rounded-sm p-3 text-sm">{error}</div>}
        <button type="submit" disabled={submitting}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-sm disabled:opacity-60"
          data-testid="rate-submit">
          {submitting ? <><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Submitting…</> : "Submit review"}
        </button>
      </form>
    </Frame>
  );
}

function Frame({ children }) {
  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <header className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <div className="w-11 h-11 bg-slate-900 text-white flex items-center justify-center rounded-sm font-black text-lg" style={{ fontFamily: "Chivo" }}>HK</div>
          <div>
            <div className="hk-tiny-label leading-none" style={{ color: "#E52B50" }}>Hygiene</div>
            <div className="font-black text-lg leading-tight text-slate-900" style={{ fontFamily: "Chivo" }}>KITCHENS</div>
          </div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-10">{children}</main>
    </div>
  );
}

function StarRow({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-800 mb-2">{label}</label>
      <div className="flex items-center gap-2" data-testid={testId}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testId}-${n}`}
            className={`w-10 h-10 rounded-sm flex items-center justify-center transition-colors ${n <= value ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}>
            <Star className="w-5 h-5" fill={n <= value ? "white" : "none"} />
          </button>
        ))}
        <span className="ml-3 text-xs text-slate-500">{value ? `${value} / 5` : "Tap a star"}</span>
      </div>
    </div>
  );
}
